import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import { cn } from '../../lib/cn.js';

/**
 * shadcn Badge, carrying the admin's semantic tones rather than stock
 * shadcn's.
 *
 * The variants encode URGENCY, not category (the rule `ORDER_STATUS_TONE`
 * already follows): amber means the merchant has to do something, green means
 * money arrived, grey means waiting on somebody else. A merchant scanning a
 * list of forty orders reads the colour, not the word.
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[0.72rem] font-semibold ' +
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-3 [&_svg]:shrink-0",
  {
    variants: {
      tone: {
        neutral: 'bg-[var(--color-canvas)] text-[var(--color-ink-soft)]',
        brand: 'bg-[var(--color-brand-soft)] text-[var(--color-brand)]',
        positive: 'bg-[var(--color-positive-soft)] text-[var(--color-positive)]',
        warn: 'bg-[var(--color-warn-soft)] text-[var(--color-warn)]',
        danger: 'bg-[var(--color-danger-soft)] text-[var(--color-danger)]',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

export type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>['tone']>;

export function Badge({
  className,
  tone,
  ...props
}: ComponentProps<'span'> & VariantProps<typeof badgeVariants>) {
  return <span data-slot="badge" className={cn(badgeVariants({ tone }), className)} {...props} />;
}

export { badgeVariants };
